import { Component } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs/internal/Subscription';
import { Point } from 'src/app/shared/atlas-gazetteer/models/point.model';
import { MapService } from 'src/app/shared/atlas-mapping/services/map.service';
import * as fromSmStore from 'src/app/spatial-modeller-store';
import * as fromLocatorStore from 'src/app/locator-store';
import * as fromProfilerStore from 'src/app/profiler-store';
import * as fromCore from 'src/app/core/store';
import { LayoutService } from 'src/app/shared/atlas-layout/services/layout.service';
import { measureOptions } from 'src/app/shared/utils/map-utils';
import {
  getAppSelectedFeature,
  getDefaultDistanceUnit
} from 'src/app/core/store';
import { SelectedFeature } from 'src/app/core/models/feature';
import { MapLayerStyleService } from '../../services/map-layer-style-service';
import { DialogWidth } from 'src/app/shared/atlas-dialog/enums/dialog-width.enum';
import { DialogService } from 'src/app/core/services/dialog.service';
import { AiAssistantDialogComponent } from 'src/app/core/components/ai-assistant/ai-assistant-dialog/ai-assistant-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { AiPromptTemplateLibraryService } from 'src/app/core/services/ai-prompt-template-library.service';
import { isAiAssistantDialogOpen } from 'src/app/shared/utils/dialog-utils';
import { ShareMapDialogComponent } from '../share-map-dialog/share-map-dialog.component';

@Component({
  selector: 'atlas-map-control-panel',
  templateUrl: './map-control-panel.component.html',
  styleUrl: './map-control-panel.component.less',
  standalone: false
})
export class MapControlPanelComponent {
  private subscription = new Subscription();
  selectedLocation: Point;
  showButtons: boolean = false;
  isAiAssistantEnabled$ = this.smStore$.select(fromCore.isAiAssistantEnabled);
  isShareMapEnabled: boolean = false;
  showShareMap: boolean = false;

  selectedSupplypoint$ = this.smStore$.select(
    fromSmStore.getSpatialModellerSupplyPoint
  );

  selectedLocatorLocation$ = this.locatorStore$.select(
    fromLocatorStore.getSelectedLocation
  );
  centrePoint: Point;
  centerPointView$ = this.mapService.centreViewCoordinates$;
  measurement: 0;

  defaultDistanceUnit = '';
  is3D = false;
  selectedFeature: string | null = null;
  selectedLocatorLibraryId = 0;
  selectedLocatorLibraryName = '';
  selectedProfilerLibraryId = 0;
  selectedProfilerLibraryName = '';
  selectedCatchmentName = '';
  selectedLocationName = '';
  selectedLocationId: number | null = null;
  selectedShapeIds: number[] | null = null;

  constructor(
    public mapService: MapService,
    private smStore$: Store<fromSmStore.State>,
    private locatorStore$: Store<fromLocatorStore.State>,
    private profilerStore$: Store<fromProfilerStore.State>,
    private dialogService: DialogService,
    public layoutService: LayoutService,
    public mapLayerStyleService: MapLayerStyleService,
    private dialog: MatDialog,
    private aiPromptService: AiPromptTemplateLibraryService
  ) {}

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnInit(): void {
    this.subscription.add(
      this.selectedSupplypoint$.subscribe((supplyPoint) => {
        if (supplyPoint) {
          this.selectedLocation = supplyPoint.location;
        }
      })
    );

    this.subscription.add(
      this.selectedLocatorLocation$.subscribe((locatorLocation) => {
        this.selectedLocationName = locatorLocation?.name ?? '';
        if (locatorLocation) {
          const location = {
            latitude: locatorLocation.latitude!,
            longitude: locatorLocation.longitude!
          };
          this.selectedLocation = location;
        }
      })
    );

    this.subscription.add(
      this.centerPointView$.subscribe((coordinates) => {
        this.centrePoint = {
          latitude: coordinates[1],
          longitude: coordinates[0]
        };
      })
    );

    this.subscription.add(
      this.mapService.measureLayerValue$.subscribe((measurement) => {
        this.measurement = measurement;
      })
    );

    this.subscription.add(
      this.mapService.pitch$.subscribe((pitch) => {
        this.is3D = pitch === 60;
      })
    );

    this.subscription.add(
      this.smStore$.select(getDefaultDistanceUnit).subscribe((unit) => {
        this.defaultDistanceUnit = unit;
      })
    );

    this.subscription.add(
      this.smStore$
        .select(fromCore.getShowShareMap)
        .subscribe((showShareMap) => {
          this.showShareMap = showShareMap;
        })
    );

    this.subscription.add(
      this.smStore$.select(getAppSelectedFeature).subscribe((feature) => {
        this.isShareMapEnabled =
          feature === SelectedFeature.Locator ||
          feature === SelectedFeature.Profiler;
        this.selectedFeature = this.isShareMapEnabled ? feature : null;
        this.onCloseClicked();
      })
    );

    this.subscription.add(
      this.locatorStore$
        .select(fromLocatorStore.getLocatorLibraryId)
        .subscribe((libraryId) => (this.selectedLocatorLibraryId = libraryId))
    );

    this.subscription.add(
      this.locatorStore$
        .select(fromLocatorStore.getLocatorLibraryName)
        .subscribe((name) => (this.selectedLocatorLibraryName = name))
    );

    this.subscription.add(
      this.profilerStore$
        .select(fromProfilerStore.getProfilerLibraryId)
        .subscribe((libraryId) => (this.selectedProfilerLibraryId = libraryId))
    );

    this.subscription.add(
      this.profilerStore$
        .select(fromProfilerStore.getProfilerLibraryName)
        .subscribe((name) => (this.selectedProfilerLibraryName = name))
    );

    this.subscription.add(
      this.locatorStore$
        .select(fromLocatorStore.getSelectedLocationId)
        .subscribe((locationId) => (this.selectedLocationId = locationId))
    );

    this.subscription.add(
      this.locatorStore$
        .select(fromLocatorStore.getSelectedShapes)
        .subscribe((shapes) => {
          this.selectedShapeIds = shapes?.length
            ? shapes.map((shape) => shape.id)
            : null;
          this.selectedCatchmentName =
            shapes?.length === 1 ? (shapes[0].name?.trim() ?? '') : '';
        })
    );
  }

  onZoomInClicked() {
    const point = this.getInitialViewPoint();
    this.mapService.zoomMap(point, this.mapService.zoom + 1);
  }

  onZoomOutClicked() {
    const point = this.getInitialViewPoint();
    this.mapService.zoomMap(point, this.mapService.zoom - 1);
  }

  getInitialViewPoint(): Point {
    return this.selectedLocation ? this.selectedLocation : this.centrePoint;
  }
  onAiAssistantClicked(): void {
    this.aiPromptService.setCurrentPromptText('');

    if (!isAiAssistantDialogOpen(this.dialog)) {
      this.dialog.open(AiAssistantDialogComponent, {
        width: DialogWidth.Large,
        panelClass: 'dialog-full-width-height',
        disableClose: true,
        hasBackdrop: false
      });
    }
  }
  onScreenshotClicked(): void {
    this.showButtons = true;
  }
  onCopydMapClicked() {
    this.mapService.takeMapScreenshot('Clipboard');
  }
  onShareMapClicked() {
    const locatorTitle = this.selectedShapeIds?.length
      ? this.selectedCatchmentName || this.selectedLocationName
      : this.selectedLocatorLibraryName;

    this.dialogService.show(ShareMapDialogComponent, {
      width: DialogWidth.Small,
      panelClass: 'dialog-full-width-height',
      data: {
        mapTitle:
          this.selectedFeature === SelectedFeature.Locator
            ? locatorTitle
            : this.selectedProfilerLibraryName,
        selectedFeature: this.selectedFeature,
        selectedLibraryId: this.getSelectedLibraryId(),
        selectedLocationId:
          this.selectedFeature === SelectedFeature.Locator
            ? this.selectedLocationId
            : null,
        selectedShapeIds:
          this.selectedFeature === SelectedFeature.Locator
            ? this.selectedShapeIds
            : null,
        latitude: this.getShareMapPoint().latitude,
        longitude: this.getShareMapPoint().longitude,
        zoomLevel: Math.round(this.mapService.zoom)
      },
      disableClose: true
    });
  }
  onDownloadMapClicked() {
    this.mapService.takeMapScreenshot('Download');
  }
  onCloseClicked(): void {
    this.showButtons = false;
    this.mapService.clearMeasureMapMode();
    this.mapService.setMeasureMapMode(false);
    this.mapService.updateMouseCursor('default');
  }

  onMeasuredMapClicked(option: measureOptions) {
    this.mapService.measureMapMode(option, this.defaultDistanceUnit);
    this.mapService.setMeasureMapMode(true);
    this.mapService.updateMouseCursor('crosshair');
  }

  onMeasuredMapCloseClicked() {
    this.mapService.clearMeasureMapMode();
    this.mapService.setMeasureMapMode(false);
    this.mapService.updateMouseCursor('default');
  }

  onToggleMap3d(): void {
    this.is3D = !this.is3D;
    this.mapService.pitchMap(this.is3D ? 60 : 0); // Adjust pitch for 3D effect
  }

  private getSelectedLibraryId(): number {
    if (this.selectedFeature === SelectedFeature.Locator) {
      return this.selectedLocatorLibraryId;
    }

    if (this.selectedFeature === SelectedFeature.Profiler) {
      return this.selectedProfilerLibraryId;
    }

    return 0;
  }

  private getShareMapPoint(): Point {
    return (
      this.centrePoint || this.selectedLocation || { latitude: 0, longitude: 0 }
    );
  }
}
